import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ClientTestimonial from '../components/ClientTestimonial';
import SEO from '../components/SEO';
import { ArrowRight, Compass, Brain, BarChart3, Layers, CheckCircle, FileText, Users, Target, Shield } from 'lucide-react';

const impactASPlusLogo = "/opt/brand/impactas-final-2.webp";

const whenOrganisationsEngage = [
  "Make sense of leadership or cultural challenges that are affecting outcomes",
  "Align leadership behaviour with strategy, values, and governance expectations",
  "Navigate periods of change, growth, or organisational transition",
  "Address behavioural risk, conduct, or ethical concerns",
  "Strengthen decision-making, accountability, and execution discipline"
];

const advisoryPhilosophy = [
  "Leadership behaviour and decision dynamics",
  "Cultural patterns and informal norms",
  "Governance, accountability, and role clarity",
  "How strategy is interpreted and enacted in practice"
];

const insightLedAllows = [
  "Move beyond anecdote and assumption",
  "Surface patterns across individuals, teams, and the organisation",
  "Identify where change is required, and where it is not",
  "Prioritise interventions based on evidence"
];

const commonFocusAreas = [
  "Leadership alignment and effectiveness",
  "Organisational culture and behavioural dynamics",
  "Ethics, conduct, and tone from the top",
  "Decision-making, accountability, and execution discipline",
  "Strategy sense-making and leadership alignment",
  "Board and executive dynamics"
];

const integrationSupports = [
  "RESILIGENT proprietary diagnostics, including DISC Lens Africa™, Leadership Mirror™, and CulturePulse™",
  "Integrated insight reports via Impact AS Plus™",
  "Targeted coaching for individuals, teams, or leadership groups",
  "Focused leadership development interventions"
];

const sustainedChangeOutcomes = [
  "Clear leadership expectations",
  "Aligned behaviours and decisions",
  "Appropriate governance and accountability",
  "Realistic implementation pathways"
];

const typicalBeginnings = [
  "A diagnostic or insight review",
  "A focused leadership or culture assessment",
  "A facilitated sense-making discussion with senior leadership"
];

const resiligentRole = [
  "Provide clarity where there is ambiguity",
  "Challenge constructively where needed",
  "Support leaders in making better decisions",
  "Help organisations move forward with confidence"
];

const AdvisoryPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Leadership Advisory & Organisational Change Nigeria | RESILIGENT Lagos"
        description="Strategic leadership advisory and organisational change consulting in Nigeria. Expert guidance on culture transformation, governance, restructuring, and leadership transitions for Nigerian corporations, banks, and multinationals. Lagos-based, Africa-wide delivery."
        keywords="organisational change consulting Nigeria, leadership advisory Lagos, change management Nigeria, culture transformation Nigeria, corporate governance consulting Lagos, organisational restructuring Nigeria, leadership transition advisory Africa, strategic consulting Nigeria"
        canonical="/advisory"
      />
      <Header />
      
      <main>
        {/* Hero Section with Background Image */}
        <section 
          className="relative min-h-[70vh] lg:min-h-[75vh] flex items-center bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(10, 10, 10, 0.95) 0%, rgba(10, 10, 10, 0.8) 40%, rgba(10, 10, 10, 0.4) 100%), url('/opt/photo/ext/u1653565685060-e15e492a7fda.webp')`
          }}
        >
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8 pt-24">
            <div className="max-w-3xl">
              <h1 className="text-white font-semibold text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight mb-8">
                Advisory & Organisational Change
              </h1>
              
              <p className="text-[#2693d0] text-lg lg:text-xl font-medium leading-relaxed mb-6">
                Supporting organisations through complex leadership and behavioural change
              </p>
              
              <p className="text-slate-300 text-base lg:text-lg leading-relaxed mb-6">
                RESILIGENT works with organisations facing moments where leadership behaviour, culture, and organisational design directly affect performance, trust, and execution.
              </p>
              
              <p className="text-slate-300 text-base lg:text-lg xl:text-xl leading-relaxed max-w-2xl">
                Our advisory work focuses on human change: how leaders think, decide, behave, and lead, particularly in environments shaped by complexity, growth, risk, and transformation.
              </p>
            </div>
          </div>
        </section>

        {/* When Organisations Engage Us */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                  When Organisations Engage Us at an Advisory Level
                </h2>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                  Organisations engage RESILIGENT's advisory support when they need to:
                </p>
              </div>
              
              <div>
                <ul className="space-y-4 mb-8">
                  {whenOrganisationsEngage.map((item, index) => (
                    <li key={index} className="flex items-start gap-4">
                      <CheckCircle className="w-5 h-5 text-[#2693d0] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base lg:text-lg leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-[#f0f5f8] rounded-xl p-6 border border-slate-100">
                  <p className="text-slate-800 font-medium text-base lg:text-lg leading-relaxed">
                    Our role is to help organisations see clearly, decide deliberately, and act proportionately.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Advisory Philosophy */}
        <section className="relative bg-[#f0f5f8] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                Our Advisory Philosophy
              </h2>
              
              <p className="text-slate-800 font-medium text-lg lg:text-xl leading-relaxed mb-8">
                We do not approach organisational change as a generic transformation exercise.
              </p>
              
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                Instead, we focus on:
              </p>
              
              <ul className="space-y-3 mb-10">
                {advisoryPhilosophy.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-700 text-base lg:text-lg leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
              
              <div className="bg-white rounded-xl p-6 card-pulse-border">
                <p className="text-slate-800 font-semibold text-lg lg:text-xl text-center">
                  Change is treated as a leadership and behavioural challenge, not a communications or restructuring exercise.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Insight-led Advisory */}
        <section className="bg-[#1e3a5f] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <img 
                  src={impactASPlusLogo} 
                  alt="Impact AS Plus" 
                  className="h-12 lg:h-14 w-auto mb-8"
                />
                <h2 className="text-white font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                  Insight-Led Advisory
                </h2>
                <p className="text-slate-200 text-base lg:text-lg leading-relaxed">
                  All advisory engagements are grounded in diagnostic insight, most commonly through Impact AS Plus™.
                </p>
              </div>
              
              <div>
                <p className="text-slate-300 text-base lg:text-lg leading-relaxed mb-6">
                  This allows us to:
                </p>
                <ul className="space-y-4 mb-8">
                  {insightLedAllows.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#2693d0] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-200 text-base lg:text-lg leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <p className="text-slate-200 font-medium text-base lg:text-lg leading-relaxed">
                    Insight provides the discipline that prevents over-reaction and under-action.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Client Testimonial - Chris Omo-Osagie */}
            <div className="mt-16 max-w-3xl mx-auto">
              <ClientTestimonial
                quote="The leadership development programme strengthened strategic thinking and collaboration at senior levels, delivering measurable impact across our operations."
                name="Chris Omo-Osagie"
                title="CFO"
                company="Segilola Resources & Thor Explorations"
                variant="dark"
              />
            </div>
          </div>
        </section>

        {/* Common Advisory Focus Areas */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                  Common Advisory Focus Areas
                </h2>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                  RESILIGENT supports organisations across a range of advisory and change contexts, including:
                </p>
              </div>
              
              <div>
                <div className="grid sm:grid-cols-2 gap-4 mb-8">
                  {commonFocusAreas.map((item, index) => (
                    <div key={index} className="bg-[#f0f5f8] rounded-lg p-4 border border-slate-100">
                      <span className="text-slate-700 text-sm font-medium">{item}</span>
                    </div>
                  ))}
                </div>
                <p className="text-slate-600 text-base leading-relaxed">
                  Engagements are tailored to organisational context and maturity, rather than delivered as standardised programmes.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Integrating Diagnostics, Coaching, and Development */}
        <section className="relative bg-[#f0f5f8] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                Integrating Diagnostics, Coaching, and Development
              </h2>
              
              <p className="text-slate-800 font-medium text-lg lg:text-xl leading-relaxed mb-8">
                Advisory work rarely stands alone.
              </p>
              
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                Depending on the context, it may be supported by:
              </p>
              
              <ul className="space-y-4 mb-8">
                {integrationSupports.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-700 text-base lg:text-lg leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
              
              <div className="bg-[#2693d0]/5 rounded-xl p-6 border border-[#2693d0]/20">
                <p className="text-slate-800 font-medium text-base lg:text-lg leading-relaxed">
                  This integration ensures that advisory recommendations are practical, grounded, and actionable.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* From Diagnosis to Sustained Change */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                  From Diagnosis to Sustained Change
                </h2>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                  RESILIGENT works with organisations to ensure that insight translates into:
                </p>
              </div>
              
              <div>
                <ul className="space-y-4 mb-8">
                  {sustainedChangeOutcomes.map((item, index) => (
                    <li key={index} className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-lg bg-[#2693d0]/10 flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-[#2693d0]" />
                      </div>
                      <span className="text-slate-700 text-base lg:text-lg leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-[#f0f5f8] rounded-xl p-6 border border-slate-100">
                  <p className="text-slate-800 font-medium text-base lg:text-lg">
                    We focus on sustained change, not one-off interventions.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How Advisory Engagements Typically Begin */}
        <section className="relative bg-[#f0f5f8] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                How Advisory Engagements Typically Begin
              </h2>
              
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                Most advisory engagements begin with:
              </p>
              
              <ul className="space-y-3 mb-8">
                {typicalBeginnings.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-700 text-base lg:text-lg leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
              
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                From there, the scope is refined based on evidence, urgency, and organisational readiness.
              </p>
            </div>
          </div>
        </section>

        {/* The Role of RESILIGENT */}
        <section className="bg-white py-20 lg:py-28 border-t border-slate-100">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                  The role of RESILIGENT
                </h2>
                <p className="text-slate-800 font-medium text-lg lg:text-xl leading-relaxed">
                  Our role is not to prescribe solutions or impose models.
                </p>
              </div>
              
              <div>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                  It is to:
                </p>
                <ul className="space-y-4">
                  {resiligentRole.map((item, index) => (
                    <li key={index} className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-lg bg-[#2693d0]/10 flex items-center justify-center flex-shrink-0">
                        <Compass className="w-4 h-4 text-[#2693d0]" />
                      </div>
                      <span className="text-slate-700 text-base lg:text-lg leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-[#e8f2f7] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                Next Steps
              </h2>
              
              {/* Primary CTA */}
              <div className="bg-white card-pulse-border rounded-xl p-8 mb-6 shadow-sm">
                <h3 className="text-slate-800 font-semibold text-xl mb-4">
                  Request an Advisory Conversation
                </h3>
                <p className="text-slate-600 text-base leading-relaxed mb-6">
                  A confidential discussion to explore your organisational context, priorities, and potential next steps.
                </p>
                <a
                  href="mailto:info@resiligent.com?subject=Advisory%20Conversation%20Request"
                  className="inline-flex items-center gap-2 bg-[#2693d0] text-white px-6 py-3.5 rounded font-medium text-base hover:bg-[#1e7ab8] transition-all duration-300"
                >
                  Request a Conversation
                  <ArrowRight size={18} />
                </a>
              </div>

              {/* Secondary CTA */}
              <a
                href="/impact-as-plus"
                className="inline-flex items-center gap-3 text-slate-600 hover:text-[#2693d0] transition-colors duration-300"
              >
                <FileText className="w-5 h-5" />
                <span className="text-base font-medium">Explore Impact AS Plus™</span>
                <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </section>

        {/* Footer Note */}
        <section className="bg-[#e8f2f7] pb-12 border-t border-slate-200">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8 pt-8">
            <p className="text-slate-500 text-sm">
              Advisory engagements are designed in partnership with client leadership and governed by appropriate professional and ethical standards.
            </p>
          </div>
        </section>
      </main>

      <Footer hidePreFooterCta={true} />
    </div>
  );
};

export default AdvisoryPage;
